import { useEffect } from "react";
import { brand, buildSchema, getPage } from "../siteContent";

const DEFAULT_KEYWORDS =
  "CSS preparation Pakistan, CSS 2027, PMS preparation Pakistan, MPT preparation, CSS syllabus, CSS past papers";

function upsertMetaByName(name, content) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function upsertMetaByProperty(property, content) {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function removeManagedMeta(selector) {
  document.querySelectorAll(`${selector}[data-aspirant-managed="true"]`).forEach((meta) => meta.remove());
}

function upsertMetaListByProperty(property, values) {
  removeManagedMeta(`meta[property="${property}"]`);
  values.forEach((value) => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", property);
    meta.setAttribute("content", value);
    meta.setAttribute("data-aspirant-managed", "true");
    document.head.appendChild(meta);
  });
}

function upsertLink(rel, href, extraAttributes = {}) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
  Object.entries(extraAttributes).forEach(([key, value]) => link.setAttribute(key, value));
}

export function Seo({ page, path }) {
  useEffect(() => {
    const activePage = page || getPage(path);
    const canonicalUrl = `${brand.url}${path === "/" ? "/" : path}`;
    const imageUrl = brand.cover.startsWith("http") ? brand.cover : `${brand.url}${brand.cover}`;
    const title = activePage.title ? `${activePage.title} | ${brand.name}` : brand.name;
    const tags = Array.isArray(activePage.tags) ? activePage.tags : [];
    const keywordContent = activePage.keywords || tags.join(", ") || DEFAULT_KEYWORDS;
    const topicContent = tags.slice(0, 6).join(", ") || "CSS preparation Pakistan";
    const isArticle = ["guide", "blog", "answer-tip"].includes(activePage.kind);

    document.title = title;
    upsertMetaByName("description", activePage.description || "");
    upsertMetaByName("keywords", keywordContent);
    upsertMetaByName("news_keywords", keywordContent);
    upsertMetaByName("subject", topicContent);
    upsertMetaByName("topic", topicContent);
    upsertMetaByName("classification", activePage.kind || "website");
    upsertMetaByName("author", brand.name);
    upsertMetaByName("application-name", brand.name);
    upsertMetaByName("apple-itunes-app", "app-id=6792441265");
    upsertMetaByName("publisher", brand.name);
    upsertMetaByName(
      "robots",
      activePage.noindex
        ? "noindex,nofollow"
        : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
    );
    upsertMetaByProperty("og:type", isArticle ? "article" : "website");
    upsertMetaByProperty("og:locale", "en_PK");
    upsertMetaByProperty("og:site_name", brand.name);
    upsertMetaByProperty("og:title", title);
    upsertMetaByProperty("og:description", activePage.description || "");
    upsertMetaByProperty("og:url", canonicalUrl);
    upsertMetaByProperty("og:image", imageUrl);
    upsertMetaByProperty("og:image:secure_url", imageUrl);
    upsertMetaByProperty("og:image:type", "image/jpeg");
    upsertMetaByProperty("og:image:alt", `${brand.name} CSS, MPT and PMS preparation app`);
    upsertMetaByName("twitter:card", "summary_large_image");
    upsertMetaByName("twitter:title", title);
    upsertMetaByName("twitter:description", activePage.description || "");
    upsertMetaByName("twitter:url", canonicalUrl);
    upsertMetaByName("twitter:image", imageUrl);
    upsertMetaByName("twitter:image:alt", `${brand.name} CSS, MPT and PMS preparation app`);

    if (isArticle) {
      upsertMetaByProperty("article:section", activePage.category || activePage.kind);
      upsertMetaByProperty("article:published_time", activePage.datePublished || "2026-06-15");
      upsertMetaByProperty("article:modified_time", activePage.dateModified || "2026-06-15");
      upsertMetaListByProperty("article:tag", tags.slice(0, 14));
    } else {
      removeManagedMeta(`meta[property="article:tag"]`);
    }

    upsertLink("canonical", canonicalUrl);
    upsertLink("alternate", canonicalUrl, { hreflang: "en-PK" });
    const logoUrl = brand.logo.startsWith("http") ? brand.logo : `${brand.url}${brand.logo}`;
    upsertLink("icon", logoUrl);
    upsertLink("apple-touch-icon", logoUrl);

    const previousScript = document.getElementById("seo-json-ld");
    if (previousScript) {
      previousScript.remove();
    }

    const script = document.createElement("script");
    script.id = "seo-json-ld";
    script.type = "application/ld+json";
    script.text = JSON.stringify(buildSchema(path, activePage));
    document.head.appendChild(script);
  }, [page, path]);

  return null;
}
